import { extendObservable, reaction } from 'mobx';
import PropTypes from 'prop-types';
import { apiCall } from '../API';
import AsyncActionStore from './AsyncActionStore';
import BoolStore from './helpers/BoolStore';

export const CONCURRENCY_ISSUE = {
    EDIT_AFTER_EDIT: 'edit_after_edit',
    EDIT_AFTER_DELETE: 'edit_after_delete',
    DELETE_AFTER_EDIT: 'delete_after_edit',
};

export default class ConcurrencyPopUpStore {
    static _PropType = {
        status: AsyncActionStore.PropType.isRequired,
        isOpen: BoolStore.PropTypes.isRequired,
        apiGetLatestRevision: PropTypes.func.isRequired,
        type: PropTypes.string,
        apiVersionedUpdate: PropTypes.func.isRequired,
        onConfirm: PropTypes.func.isRequired,
        apiUpdate: PropTypes.func.isRequired,
        concurrencyIssue: PropTypes.oneOf(Object.values(CONCURRENCY_ISSUE)),
        revision: PropTypes.shape({
            actorEmail: PropTypes.string,
            revisionId: PropTypes.number,
        }),
        entity: PropTypes.object,
        apiCreate: PropTypes.func,
        apiVersionedDelete: PropTypes.func,
        apiDelete: PropTypes.func,
    };

    static CONCURRENCY_MODAL_CANCEL_LABEL = 'ConcurrencyModalCancel';

    static PropType = PropTypes.shape(ConcurrencyPopUpStore._PropType);

    constructor(data = { }) {
        extendObservable(this, /** @class ConcurrencyPopUpStore */{
            apiGetLatestRevision: (type, id) => apiCall(`/console/audit/latestRevision/${type}/${id}`)(),
            status: new AsyncActionStore(),
            isOpen: new BoolStore(false),
            concurrencyIssue: null,
            revision: null,
            // coming from caller
            type: null,
            apiVersionedDelete: null,
            apiDelete: null,
            apiUpdate: null,
            apiCreate: null,
            apiVersionedUpdate: null,
            onFinish: () => {},
            onCancel: () => {}
        }, data);

        reaction(() => !!this.concurrencyIssue, (active) => {
            if (active) {
                // If this fails, we are stuck.
                this.apiGetLatestRevision(this.type, this.entity.id).then((r) => {
                    this.revision = r;
                    this.isOpen.setValue(true);
                });
            } else {
                this.isOpen.setValue(false);
                this.revision = null;
            }
        });
    }

    startAction = (item, onFinish, onCancel) => {
        this.entity = item;
        this.onFinish = onFinish;
        this.onCancel = onCancel;
    }

    finishAction = (result) => {
        this.close();
        this.onFinish(result);
    }

    update = (item, onFinish, onCancel) => {
        this.startAction(item, onFinish, onCancel);
        return this.apiVersionedUpdate(this.entity).then(this.finishAction).catch((response) => {
            if (this.isNotFound(response)) {
                this.concurrencyIssue = CONCURRENCY_ISSUE.EDIT_AFTER_DELETE;
                return;
            }
            if (this.isConflict(response)) {
                this.concurrencyIssue = CONCURRENCY_ISSUE.EDIT_AFTER_EDIT;
                return;
            }
            throw response;
        });
    }

    delete = (item, onFinish, onCancel) => {
        this.startAction(item, onFinish, onCancel);

        return this.apiVersionedDelete(this.entity).then(this.finishAction).catch((response) => {
            if (this.isNotFound(response)) {
                // All good - someone must've deleted it before us. Ignore error
                this.finishAction();
                return;
            }
            if (this.isConflict(response)) {
                this.concurrencyIssue = CONCURRENCY_ISSUE.DELETE_AFTER_EDIT;
                return;
            }
            throw response;
        });
    }

    onConfirm = () => {
        const action = {
            [CONCURRENCY_ISSUE.DELETE_AFTER_EDIT]: this.apiDelete,
            [CONCURRENCY_ISSUE.EDIT_AFTER_EDIT]: this.apiUpdate,
            [CONCURRENCY_ISSUE.EDIT_AFTER_DELETE]: this.apiCreate,
        }[this.concurrencyIssue];

        if (!this.status.isLoading) {
            this.status.setLoading(action(this.entity).then(this.finishAction));
        }
        return this.status.promise;
    }

    isConflict = (response) =>
        response.status === 409;

    isNotFound = (response) =>
        response.status === 404;

    close = () => {
        this.concurrencyIssue = null;
    }

    cancel = () => {
        this.close();
        this.onCancel?.();
    }
}
